/**
 * Product type bitwise enum
 * Bitwise enum to represent the product types that can be selected in the map.
 * To toggle a product type, use the bitwise XOR operator (e.g. `productType ^ ProductType.ACCOMMODATION`).
 * To check if a product type is selected, use the bitwise AND operator (e.g. `productType & ProductType.ACCOMMODATION`).
 */
export enum ProductType {
  NONE = 0,
  ACCOMMODATION = 1 << 0,
  EXPERIENCE = 1 << 1,
  BEACH_SECTION = 1 << 2,
  BIKE = 1 << 3,
}

enum FilterInputProductType {
  ACCOMMODATION = 'accommodation',
  EXPERIENCE = 'experience',
  BEACH_SECTION = 'beach_chair',
  BIKE = 'bike',
}

/**
 * Maps the bitwise product type to the product type string used in the filter.
 * @param productType - The bitwise product type to map.
 * @returns The mapped product type string or null if none, all or more than one product type is selected.
 */
export const mapProductTypeToFilterInput = (productType: ProductType) => {
  switch (productType) {
    case ProductType.ACCOMMODATION:
      return FilterInputProductType.ACCOMMODATION
    case ProductType.EXPERIENCE:
      return FilterInputProductType.EXPERIENCE
    case ProductType.BEACH_SECTION:
      return FilterInputProductType.BEACH_SECTION
    case ProductType.BIKE:
      return FilterInputProductType.BIKE
    case ProductType.NONE:
    default:
      return null
  }
}

/**
 * Checks if multiple product types are selected.
 * @param productType - The bitwise product type to check.
 * @returns True if multiple product types are selected, false otherwise.
 */
export const hasMultipleProductTypes = (productType: ProductType) =>
  (productType & (productType - 1)) !== 0

/**
 * Checks if a product type is selected.
 * @param productType - The bitwise product type to check.
 * @param type - The product type to check for.
 * @returns True if the product type is selected, false otherwise.
 */
export const hasProductType = (productType: ProductType, type: ProductType) =>
  (productType & type) !== 0
